'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { motion, useScroll, useTransform, useMotionTemplate, type Variants } from 'framer-motion';
import { ArrowRight, Sparkles, Users, Star, Zap } from 'lucide-react';

const container: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
};

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' as const } },
};

// Coach conversation messages — staggered reveal
const COACH_LINES = [
  { from: 'coach', text: '안녕하세요! 반가워요 😊', delay: 0.6 },
  { from: 'coach', text: '저는 당신의 성장을 함께하는 AI 코치예요.', delay: 1.4 },
  { from: 'user', text: '어떤 도움을 받을 수 있어요?', delay: 2.4 },
  { from: 'coach', text: '매일의 기록에서 패턴을 읽고,\n나만의 성장 전략을 만들어드려요.', delay: 3.2 },
  { from: 'coach', text: '준비되셨으면, 같이 시작해볼까요? ✨', delay: 4.2 },
];

function CoachChat() {
  return (
    <div className="space-y-3">
      {COACH_LINES.map((line, i) => {
        const isCoach = line.from === 'coach';
        return (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 12, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: line.delay, duration: 0.4, ease: 'easeOut' }}
            className={`flex ${isCoach ? 'justify-start' : 'justify-end'}`}
          >
            {isCoach && (
              <div className="w-8 h-8 rounded-full flex items-center justify-center mr-2 flex-shrink-0 text-xs font-bold"
                style={{ background: 'linear-gradient(135deg, #6366f1, #4338ca)', color: 'white' }}>
                AI
              </div>
            )}
            <div
              className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed max-w-[85%] whitespace-pre-line ${
                isCoach
                  ? 'rounded-tl-sm bg-white/95 text-gray-800'
                  : 'rounded-tr-sm text-white'
              }`}
              style={!isCoach ? { background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' } : undefined}
            >
              {line.text}
            </div>
          </motion.div>
        );
      })}

      {/* Typing indicator — appears after last message */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 5.2, duration: 0.3 }}
        className="flex justify-start"
      >
        <div className="w-8 h-8 rounded-full flex items-center justify-center mr-2 flex-shrink-0 text-xs font-bold"
          style={{ background: 'linear-gradient(135deg, #6366f1, #4338ca)', color: 'white' }}>
          AI
        </div>
        <div className="bg-white/95 rounded-2xl rounded-tl-sm px-4 py-3 flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: '0ms' }} />
          <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: '150ms' }} />
          <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </motion.div>
    </div>
  );
}

interface HeroSectionProps {
  onStartClick?: () => void;
}

export default function HeroSection({ onStartClick }: HeroSectionProps) {
  // Mobile-only scroll-linked blur: chat starts soft, clears as user scrolls
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const { scrollY } = useScroll();
  const chatOpacity = useTransform(scrollY, [0, 100], [isMobile ? 0.6 : 1, 1]);
  const blurVal = useTransform(scrollY, [0, 100], [isMobile ? 2.5 : 0, 0]);
  const chatFilter = useMotionTemplate`blur(${blurVal}px)`;

  return (
    <section
      className="relative overflow-hidden flex items-center py-16 sm:py-24 lg:py-40"
      style={{ background: 'linear-gradient(170deg, #0f172a 0%, #1e1b4b 22%, #312e81 48%, #4338ca 75%, #6366f1 100%)' }}
    >
      {/* Ambient gradient blobs — heavy blur to eliminate edge artifacts */}
      <div className="absolute inset-0 -z-0" aria-hidden="true">
        <div className="absolute top-[-18%] left-[-12%] w-[50vw] h-[50vw] max-w-[600px] max-h-[600px] rounded-full blur-[120px] animate-blob" style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.3), rgba(67,56,202,0.1) 60%, transparent 80%)' }} />
        <div className="absolute bottom-[-12%] right-[-12%] w-[45vw] h-[45vw] max-w-[520px] max-h-[520px] rounded-full blur-[120px] animate-blob animation-delay-2000" style={{ background: 'radial-gradient(circle, rgba(20,184,166,0.2), rgba(6,182,212,0.08) 60%, transparent 80%)' }} />
        <div className="absolute top-[30%] right-[15%] w-[30vw] h-[30vw] max-w-[360px] max-h-[360px] rounded-full blur-[100px] animate-blob animation-delay-4000" style={{ background: 'radial-gradient(circle, rgba(168,85,247,0.15), rgba(139,92,246,0.05) 60%, transparent 80%)' }} />
      </div>

      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="relative z-10 container mx-auto px-4 max-w-6xl"
      >
        <div className="flex flex-col lg:flex-row items-center gap-12 sm:gap-14 lg:gap-16">
          {/* Left: Text content */}
          <div className="flex-1 text-center lg:text-left">
            {/* Badge */}
            <motion.div variants={fadeUp} className="mb-4 sm:mb-6">
              <span
                className="inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-full backdrop-blur-sm"
                style={{
                  background: 'rgba(255,255,255,0.12)',
                  color: 'rgba(255,255,255,0.9)',
                  border: '1px solid rgba(255,255,255,0.2)',
                }}
              >
                <Sparkles className="w-4 h-4" />
                AI 코치와 함께하는 매일의 성장
              </span>
            </motion.div>

            {/* Headline */}
            <motion.h1
              variants={fadeUp}
              className="text-[2.5rem] sm:text-5xl lg:text-6xl font-black text-white leading-[1.08] mb-4 sm:mb-5 tracking-tight"
            >
              기록하면 보인다.
              <br />
              <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(135deg, #fde68a, #fbbf24, #f59e0b)' }}>
                보이면 달라진다.
              </span>
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              variants={fadeUp}
              className="text-base sm:text-lg mb-6 sm:mb-8 max-w-lg mx-auto lg:mx-0 leading-relaxed"
              style={{ color: 'rgba(255,255,255,0.65)' }}
            >
              하루 1분, 나를 돌아보는 습관.
              <br />
              AI 코치가 나만의 성장 전략을 만들어드려요.
            </motion.p>

            {/* CTA buttons */}
            <motion.div
              variants={fadeUp}
              className="flex flex-col sm:flex-row gap-2.5 sm:gap-3 mb-6 sm:mb-0 justify-center lg:justify-start"
            >
              {onStartClick ? (
                <button
                  onClick={onStartClick}
                  className="group inline-flex items-center justify-center gap-2.5 px-6 py-3 sm:px-8 sm:py-4 text-white text-sm sm:text-base font-bold rounded-2xl transition-all hover:-translate-y-1 active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                  style={{ background: 'linear-gradient(135deg, #6366f1, #4338ca)', boxShadow: '0 8px 32px -4px rgba(99,102,241,0.5)' }}
                >
                  무료로 시작하기
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              ) : (
                <Link
                  href="/onboarding"
                  className="group inline-flex items-center justify-center gap-2.5 px-6 py-3 sm:px-8 sm:py-4 text-white text-sm sm:text-base font-bold rounded-2xl transition-all hover:-translate-y-1 active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                  style={{ background: 'linear-gradient(135deg, #6366f1, #4338ca)', boxShadow: '0 8px 32px -4px rgba(99,102,241,0.5)' }}
                >
                  무료로 시작하기
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              )}
              <Link
                href="/auth/login"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 sm:px-8 sm:py-4 text-sm sm:text-base font-bold rounded-2xl transition-all hover:-translate-y-1 active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                style={{ background: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.9)', border: '1px solid rgba(255,255,255,0.25)' }}
              >
                로그인
              </Link>
            </motion.div>

            {/* Trust indicators — hidden on mobile for breathing room */}
            <motion.div
              variants={fadeUp}
              className="mt-10 hidden sm:flex items-center justify-center lg:justify-start gap-6"
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(20,184,166,0.2)' }}>
                  <Users className="w-4 h-4" style={{ color: '#5eead4' }} />
                </div>
                <div>
                  <p className="text-sm font-bold text-white/90">4가지 모드</p>
                  <p className="text-[10px] text-white/50">맞춤 코칭</p>
                </div>
              </div>
              <div className="w-px h-8 flex-shrink-0" style={{ backgroundColor: 'rgba(255,255,255,0.12)' }} aria-hidden="true" />
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(251,191,36,0.2)' }}>
                  <Star className="w-4 h-4" style={{ color: '#fcd34d' }} />
                </div>
                <div>
                  <p className="text-sm font-bold text-white/90">6가지 도구</p>
                  <p className="text-[10px] text-white/50">매일 사용</p>
                </div>
              </div>
              <div className="w-px h-8 flex-shrink-0" style={{ backgroundColor: 'rgba(255,255,255,0.12)' }} aria-hidden="true" />
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(129,140,248,0.2)' }}>
                  <Zap className="w-4 h-4" style={{ color: '#a5b4fc' }} />
                </div>
                <div>
                  <p className="text-sm font-bold text-white/90">AI 코칭</p>
                  <p className="text-[10px] text-white/50">실시간 분석</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right: Coach conversation — scroll-linked reveal (soft → clear) */}
          <motion.div
            style={{ opacity: chatOpacity, filter: chatFilter }}
            className="flex-1 w-full max-w-md lg:max-w-lg"
          >
            <CoachChat />
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
